import { observer } from "mobx-react";
import React from "react";
import { generatePath, RouteComponentProps, withRouter } from "react-router";
import {
  SnackbarContextProps,
  withGlobalSnackbar,
} from "services/snackbar/snackbarContext";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import { Button } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";

import Page from "components/Layout/Page";
import SortingControl from "components/SortingControl";
import FilteringControl from "components/FilteringControl";
import Placeholder from "components/Placeholder";
import NavBar from "../NavBar";
import {
  PageControl,
  SearchContainer,
  SearchControls,
  SearchControlWrapper,
  Search,
  OverflowLoader,
  ScrollLoader,
  QuestionCardWrapper,
} from "./styles";

import ROUTES from "navigation/routes";
import { computed } from "mobx";
import { TableSortType } from "stores/templates/TableStore";
import InfiniteScroll from "react-infinite-scroll-component";
import { SNACKBAR_TYPES } from "components/Snackbar";
import QuestionStore from "stores/QuestionStore";
import QuestionCard from "./QuestionCard";

type Props = RouteComponentProps & GlobalPopupProps & SnackbarContextProps & {};

const HEADER_WITH_TITLE_HEIGHT = "338px";
const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class Question extends React.Component<Props> {
  @computed get questions() {
    return QuestionStore.items;
  }

  @computed get questionListIsEmpty() {
    return this.questions.length === 0;
  }

  componentDidMount() {
    QuestionStore.fetch();
  }

  componentWillUnmount() {
    QuestionStore.resetPage();
  }

  fetchMoreQuestions = async () => {
    const { setMessage } = this.props;

    try {
      await QuestionStore.fetchMoreQuestions();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    QuestionStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH && QuestionStore.fetch();
  };

  onSearchClear = () => {
    QuestionStore.clearSearch();
    QuestionStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    console.log(value);
    QuestionStore.applyOrder(value);
    QuestionStore.resetPage();
    QuestionStore.fetch();
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      QuestionStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    QuestionStore.resetPage();
    QuestionStore.fetch();
  };

  onFilterClear = () => {
    QuestionStore.clearFilters();
    QuestionStore.fetch();
  };

  onAddQuestion = () => {
    this.props.history.push({
      pathname: generatePath(ROUTES.CONTENT_QUESTIONNAIRE_QUESTION_DETAILS),
      state: { editMode: true },
    });
  };

  onDetailsClick = (quiz: any) => () => {
    const path = generatePath(ROUTES.CONTENT_QUESTIONNAIRE_QUESTION_DETAILS, {
      questionId: quiz.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: false } });
  };

  onEdit = (quiz: any) => () => {
    const path = generatePath(ROUTES.CONTENT_QUESTIONNAIRE_QUESTION_DETAILS, {
      questionId: quiz.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: true } });
  };

  onArchive = (quiz: any) => () => {
    console.log("here start");
  };

  onCopy = (quiz: any) => () => {
    console.log("here start");
  };

  onPublish = (question: any) => async () => {
    const { setMessage } = this.props;
    const { id, publishStatus } = question;

    try {
      await QuestionStore.changeQuestionStatus(id, publishStatus);
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  render() {
    return (
      <Page
        title="Question"
        topControls={<NavBar />}
        controls={
          <>
            <PageControl>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={this.onAddQuestion}
              >
                Add Question
              </Button>
            </PageControl>
          </>
        }
        isLoading={QuestionStore.isLoading}
      >
        <SearchContainer>
          <Search
            initValue={QuestionStore.search}
            onApply={this.onSearchApply}
            onClear={this.onSearchClear}
          />
          <SearchControls>
            <SearchControlWrapper>
              <SortingControl
                initValue={QuestionStore.orderContext.appliedOrderInfo}
                onApply={this.onSortApply}
              />
            </SearchControlWrapper>
            <SearchControlWrapper>
              <FilteringControl
                initValue={QuestionStore.filters}
                onApply={this.onFilterApply}
                onClear={this.onFilterClear}
                statusFiltering
              />
            </SearchControlWrapper>
          </SearchControls>
        </SearchContainer>

        {(QuestionStore.isQuestionPublishing ||
          QuestionStore.isQuestionCopying) && <OverflowLoader />}
        {!this.questionListIsEmpty ? (
          <InfiniteScroll
            dataLength={this.questions.length}
            hasMore={QuestionStore.hasMoreInfo}
            next={this.fetchMoreQuestions}
            height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
            loader={<ScrollLoader />}
          >
            {this.questions.map(quiz => (
              <QuestionCardWrapper key={quiz.id}>
                <QuestionCard
                  title={quiz.title}
                  publishStatus={quiz.publishStatus}
                  onDetailsClick={this.onDetailsClick(quiz)}
                  onArchive={this.onArchive(quiz)}
                  onEdit={this.onEdit(quiz)}
                  onCopy={this.onCopy(quiz)}
                  onPublish={this.onPublish(quiz)}
                />
              </QuestionCardWrapper>
            ))}
          </InfiniteScroll>
        ) : (
          <Placeholder
            height={280}
            width={280}
            text={
              QuestionStore.search
                ? "No matches found. Please try another search."
                : "No questions added yet."
            }
          />
        )}
      </Page>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(Question)));
